import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { resolveEnsName } from '../utils/formatAddress';
import '../styles/Navigation.css';
import logo from '../assets/logo.png';

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(mins  / 60);
  const days  = Math.floor(hours / 24);
  if (days  > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins  > 0) return `${mins}m ago`;
  return 'just now';
};

const Navigation = ({ web3, setAccount }) => {
  const [walletAddress, setWalletAddress] = useState('');
  const [displayName,   setDisplayName]   = useState('');
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [loading,       setLoading]       = useState(false);

  // ── Notifications ──
  const [alertHistory,    setAlertHistory]    = useState(() => {
    try { return JSON.parse(localStorage.getItem('alerts_history') || '[]'); }
    catch { return []; }
  });
  const [lastSeen, setLastSeen] = useState(
    () => localStorage.getItem('alerts_last_seen') || ''
  );
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  const unreadCount = alertHistory.filter(
    (a) => !lastSeen || a.firedAt > lastSeen
  ).length;

  // Re-read history when window regains focus (alerts may fire on main page)
  useEffect(() => {
    const onFocus = () => {
      try { setAlertHistory(JSON.parse(localStorage.getItem('alerts_history') || '[]')); }
      catch { setAlertHistory([]); }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Close panel when clicking outside
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const openNotifications = () => {
    const now = new Date().toISOString();
    setLastSeen(now);
    localStorage.setItem('alerts_last_seen', now);
    setNotifOpen((prev) => !prev);
  };

  const clearHistory = () => {
    localStorage.removeItem('alerts_history');
    localStorage.removeItem('alerts_last_seen');
    setAlertHistory([]);
    setLastSeen('');
    setNotifOpen(false);
  };

  // ── Wallet ──
  useEffect(() => {
    const savedAddress     = localStorage.getItem('walletAddress');
    const savedDisplayName = localStorage.getItem('displayName');
    if (savedAddress) {
      setWalletAddress(savedAddress);
      setDisplayName(savedDisplayName || savedAddress);
    }
  }, []);

  const connectWallet = async () => {
    if (!web3) return;
    try {
      setLoading(true);
      const accounts = await web3.eth.requestAccounts();
      if (accounts && accounts.length > 0) {
        const address = accounts[0];
        const message = `Mint Debut: Sign this message to confirm ownership of your wallet. Timestamp: ${new Date().toISOString()}`;
        try {
          await web3.eth.personal.sign(message, address, '');

          setWalletAddress(address);
          localStorage.setItem('walletAddress', address);

          const nameOrShort = await resolveEnsName(address);
          setDisplayName(nameOrShort);
          localStorage.setItem('displayName', nameOrShort);

          if (setAccount) setAccount(address);

          fetch(`/api/nfts/${address}`)
            .then((r) => r.json())
            .then((data) => {
              try { sessionStorage.setItem(`nft_cache_${address.toLowerCase()}`, JSON.stringify(data)); }
              catch (_) {}
            })
            .catch(() => {});
        } catch (_) {
          setWalletAddress('');
          setDisplayName('');
          localStorage.removeItem('walletAddress');
          localStorage.removeItem('displayName');
        }
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  const disconnectWallet = () => {
    setWalletAddress('');
    setDisplayName('');
    localStorage.removeItem('walletAddress');
    localStorage.removeItem('displayName');
    if (setAccount) setAccount(null);
  };

  return (
    <nav>
      <div className="logo">
        <NavLink to="/"><img src={logo} alt="Logo" /></NavLink>
      </div>
      <button className="nav-toggle" onClick={() => setMenuOpen(!menuOpen)}>☰</button>
      <ul className={menuOpen ? 'active' : 'hidden'}>
        <li><NavLink to="/about">About</NavLink></li>
        <li><NavLink to="/">Collections</NavLink></li>

        {/* Notifications bell — desktop only */}
        <li className="notif-li" ref={notifRef}>
          <button
            className={`notif-btn${unreadCount > 0 ? ' has-unread' : ''}`}
            onClick={openNotifications}
            title="Alert history"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 2a4.5 4.5 0 00-4.5 4.5V10L2 11.5h12L12.5 10V6.5A4.5 4.5 0 008 2z" />
              <path d="M6.5 12a1.5 1.5 0 003 0" />
            </svg>
            {unreadCount > 0 && (
              <span className="notif-badge">{unreadCount}</span>
            )}
          </button>

          {notifOpen && (
            <div className="notif-panel">
              <div className="notif-header">
                <span>Alert History</span>
                {alertHistory.length > 0 && (
                  <button className="notif-clear" onClick={clearHistory}>Clear all</button>
                )}
              </div>
              {alertHistory.length === 0 ? (
                <p className="notif-empty">No alerts fired yet.</p>
              ) : (
                <ul className="notif-list">
                  {alertHistory.map((a, i) => (
                    <li key={i} className="notif-item">
                      {a.imageUrl && (
                        <img className="notif-img" src={a.imageUrl} alt="" />
                      )}
                      <div className="notif-body">
                        <span className="notif-name">{a.name}</span>
                        <span className="notif-detail">
                          Floor {a.direction} {a.threshold} ETH → {a.floor.toFixed(3)} ETH
                        </span>
                      </div>
                      <span className="notif-time">{timeAgo(a.firedAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </li>

        <li>
          {loading ? (
            <div className="nav-connecting">
              <span className="nav-spinner" />
              Connecting
            </div>
          ) : walletAddress ? (
            <div className="wallet-info">
              <NavLink to="/account" className="wallet-address">{displayName}</NavLink>
              <button className="disconnect-button" onClick={disconnectWallet}>Disconnect</button>
            </div>
          ) : (
            <button id="connect-wallet" onClick={connectWallet}>CONNECT WALLET</button>
          )}
        </li>
      </ul>
    </nav>
  );
};

export default Navigation;
